import { supabase } from "@/integrations/supabase/client";
import { isVirtualLocationId } from "@/lib/virtual";

/** A local community chat row (country + optional city). */
export interface LocalCommunityChatRow {
  id: number;
  country: string;
  city: string | null;
  chat_link: string;
  label: string | null;
}

export interface LocalCommunityChatInput {
  country: string;
  city: string | null;
  chat_link: string;
  label: string | null;
}

const clean = (s: string | null | undefined): string | null => {
  const t = (s ?? "").trim();
  return t === "" ? null : t;
};

/** All chats, ordered by country then city (country-wide row first). Admin-only. */
export async function listLocalCommunityChats(): Promise<
  LocalCommunityChatRow[]
> {
  const { data, error } = await supabase
    .from("local_community_chat")
    .select("id, country, city, chat_link, label")
    .order("country", { ascending: true })
    .order("city", { ascending: true, nullsFirst: true });
  if (error) throw error;
  return (data ?? []) as LocalCommunityChatRow[];
}

export async function createLocalCommunityChat(
  input: LocalCommunityChatInput,
): Promise<void> {
  const { error } = await supabase.from("local_community_chat").insert({
    country: input.country.trim(),
    city: clean(input.city),
    chat_link: input.chat_link.trim(),
    label: clean(input.label),
  });
  if (error) throw error;
}

export async function updateLocalCommunityChat(
  id: number,
  input: LocalCommunityChatInput,
): Promise<void> {
  const { error } = await supabase
    .from("local_community_chat")
    .update({
      country: input.country.trim(),
      city: clean(input.city),
      chat_link: input.chat_link.trim(),
      label: clean(input.label),
      updated_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteLocalCommunityChat(id: number): Promise<void> {
  const { error } = await supabase
    .from("local_community_chat")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

const norm = (s: string | null | undefined): string =>
  (s ?? "").trim().toLowerCase();

/** A local community chat row, keyed by country + optional city (NULL = country-wide). */
export interface LocalCommunityChat {
  id: number;
  country: string;
  city: string | null;
  chat_link: string;
  label: string | null;
}

/**
 * Every local community chat. The table is small, so we fetch it whole and match
 * client-side (a chat is relevant to a person by the country/city of their home
 * and participation locations).
 */
export async function fetchLocalCommunityChats(): Promise<
  LocalCommunityChat[]
> {
  const { data, error } = await supabase
    .from("v_local_community_chat")
    .select("id, country, city, chat_link, label");
  if (error) throw error;
  return (
    (data ?? []) as Array<{
      id: number | null;
      country: string | null;
      city: string | null;
      chat_link: string | null;
      label: string | null;
    }>
  )
    .filter(
      (c) =>
        c.id !== null &&
        (c.country ?? "").trim() !== "" &&
        (c.chat_link ?? "").trim() !== "",
    )
    .map((c) => ({
      id: c.id as number,
      country: (c.country as string).trim(),
      city: c.city && c.city.trim() !== "" ? c.city.trim() : null,
      chat_link: (c.chat_link as string).trim(),
      label: c.label && c.label.trim() !== "" ? c.label.trim() : null,
    }));
}

/**
 * Best chat link for a single location: a city-level chat is preferred, falling
 * back to the country-wide chat. Returns null when nothing matches.
 */
export function bestChatForLocation(
  chats: LocalCommunityChat[],
  country: string | null,
  city: string | null,
): string | null {
  const c = norm(country);
  if (!c) return null;
  const cityMatch = chats.find(
    (ch) =>
      ch.city !== null &&
      norm(ch.country) === c &&
      norm(ch.city) === norm(city),
  );
  if (cityMatch) return cityMatch.chat_link;
  const countryMatch = chats.find(
    (ch) => ch.city === null && norm(ch.country) === c,
  );
  return countryMatch ? countryMatch.chat_link : null;
}

/**
 * A local community chat matched to the user, ready to render as a labelled link.
 */
export interface CommunityLink {
  id: number;
  label: string;
  chat_link: string;
}

/**
 * Local community chats relevant to the person: matched by the country/city of
 * their home location and every location they've taken part in (past or
 * upcoming). A city chat matches its exact country+city; a country-wide chat
 * (no city) matches any associated location in that country. Deduped and sorted.
 * Powers the "Join local community in …" line.
 */
export async function fetchCommunityChatLinks(
  personId: number | null,
): Promise<CommunityLink[]> {
  if (personId === null) return [];

  // Home location + all participation locations, plus the whole (small) chat list.
  // RLS scopes participation_member and person to the caller's own rows.
  const [personRes, membersRes, chats] = await Promise.all([
    supabase
      .from("person")
      .select("home_city, home_country")
      .eq("id", personId)
      .maybeSingle(),
    supabase
      .from("participation_member")
      .select("participation!inner(location_id)")
      .eq("person_id", personId),
    fetchLocalCommunityChats(),
  ]);
  if (personRes.error) throw personRes.error;
  if (membersRes.error) throw membersRes.error;
  if (chats.length === 0) return [];

  // Associated country+city pairs and associated countries.
  const pairs = new Set<string>(); // `${country}||${city}`
  const countries = new Set<string>();
  const addPair = (country: string | null, city: string | null) => {
    const c = norm(country);
    if (!c) return;
    countries.add(c);
    if (norm(city)) pairs.add(`${c}||${norm(city)}`);
  };

  const home = personRes.data as {
    home_city: string | null;
    home_country: string | null;
  } | null;
  if (home) addPair(home.home_country, home.home_city);

  const locationIds = [
    ...new Set(
      (
        (membersRes.data ?? []) as Array<{
          participation: { location_id: number | null } | null;
        }>
      )
        .map((r) => r.participation?.location_id ?? null)
        .filter((id): id is number => !isVirtualLocationId(id)),
    ),
  ];
  if (locationIds.length > 0) {
    const { data: locs, error: locErr } = await supabase
      .from("v_location")
      .select("city, country")
      .in("id", locationIds);
    if (locErr) throw locErr;
    for (const l of (locs ?? []) as Array<{
      city: string | null;
      country: string | null;
    }>) {
      addPair(l.country, l.city);
    }
  }

  const seen = new Set<number>();
  return chats
    .filter((ch) =>
      ch.city !== null
        ? pairs.has(`${norm(ch.country)}||${norm(ch.city)}`)
        : countries.has(norm(ch.country)),
    )
    .filter((ch) => (seen.has(ch.id) ? false : (seen.add(ch.id), true)))
    .map((ch) => ({
      id: ch.id,
      label: ch.label ?? ch.city ?? ch.country,
      chat_link: ch.chat_link,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
